/**
 * UK GDPR / PECR cookie-consent helper.
 *
 * Stores the visitor's consent choice in localStorage so we don't keep
 * re-asking, and exposes a tiny pub/sub so the banner and any consumer
 * (PostHog gating, footer link, etc.) can react when the choice changes.
 *
 * Storage shape:
 *   {
 *     version: 1,
 *     timestamp: <ISO string>,
 *     categories: { essential: true, analytics: bool }
 *   }
 *
 * PECR rule: essential is always granted (site can't function without it).
 * Anything else MUST default to false until the user explicitly opts in.
 */

const STORAGE_KEY = 'guardian.cookieConsent.v1';
const CONSENT_VERSION = 1;

const listeners = new Set();

function safeGet() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== CONSENT_VERSION) return null;
    return parsed;
  } catch (err) {
    // Corrupted JSON, locked-down browser, or no localStorage at all.
    // Fall back to default (essential only) — not a fatal error.
    if (process.env.NODE_ENV !== 'production') console.warn('[consent] safeGet failed:', err);
    return null;
  }
}

function safeSet(payload) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  } catch (err) {
    // Private mode / quota exceeded / disabled storage. In-memory listeners
    // still fire, so the current session works — just won't persist.
    if (process.env.NODE_ENV !== 'production') console.warn('[consent] safeSet failed:', err);
  }
}

export function getConsent() {
  const stored = safeGet();
  if (stored) return stored;
  // Default until the visitor chooses: essential only, analytics OFF.
  return {
    version: CONSENT_VERSION,
    timestamp: null,
    categories: { essential: true, analytics: false },
  };
}

export function hasDecided() {
  return safeGet() !== null;
}

export function setConsent(categories) {
  const payload = {
    version: CONSENT_VERSION,
    timestamp: new Date().toISOString(),
    categories: {
      essential: true,
      analytics: !!categories.analytics,
    },
  };
  safeSet(payload);
  listeners.forEach((fn) => fn(payload));
  return payload;
}

export function acceptAll() {
  return setConsent({ analytics: true });
}

export function rejectAll() {
  return setConsent({ analytics: false });
}

export function resetConsent() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (err) {
    if (process.env.NODE_ENV !== 'production') console.warn('[consent] resetConsent failed:', err);
  }
  listeners.forEach((fn) => fn(null));
}

export function onConsentChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

/**
 * PostHog gate.  Our PostHog snippet in index.html now boots with
 * opt_out_capturing_by_default: true, so nothing is captured until we
 * explicitly call opt_in here.  When the user revokes analytics consent
 * we opt them back out + reset any IDs that were already stored.  We
 * also (un)load Google Analytics 4 alongside PostHog so they always
 * follow the same consent gate.
 */
export function applyPostHogConsent(categories) {
  // Lazy-import to avoid a hard dependency in tests / SSR
  let loadGA, unloadGA;
  try {
    // eslint-disable-next-line global-require
    const ga = require('./analytics');
    loadGA = ga.loadGoogleAnalytics;
    unloadGA = ga.unloadGoogleAnalytics;
  } catch (err) {
    // Analytics module optional — only available when GA is wired up.
    if (process.env.NODE_ENV !== 'production') console.warn('[consent] analytics module not loaded:', err);
  }
  if (typeof window === 'undefined') return;
  try {
    if (categories?.analytics) {
      if (window.posthog) {
        window.posthog.opt_in_capturing();
      }
      if (loadGA) loadGA();
    } else {
      if (window.posthog) {
        window.posthog.opt_out_capturing();
        if (typeof window.posthog.reset === 'function') window.posthog.reset();
      }
      if (unloadGA) unloadGA();
    }
  } catch (err) {
    // PostHog/GA SDK not finished bootstrapping. applyPostHogConsent
    // re-runs whenever consent changes, so the next call will succeed.
    if (process.env.NODE_ENV !== 'production') console.warn('[consent] applyPostHogConsent failed:', err);
  }
}
